// src/components/PlanSelectionModal.js
import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import CONFIG from '../config';
import { AuthContext } from '../contexts/AuthContext';

function PlanSelectionModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { subscriptionTier } = useContext(AuthContext);
  
  const isFree = subscriptionTier === 'free';

  if (!isOpen) return null;

  const selectPlan = (plan) => {
    // Redirect to marketing website pricing section instead of internal purchase flow
    window.open('https://www.reelintro.com/#pricing', '_blank', 'noopener,noreferrer');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl mx-4">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Choose a Plan</h2>
        
        {isFree && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-800">
                  <span className="font-bold">You're currently on the Free Plan</span>
                  <br />
                  Upgrade to unlock more videos, longer recording times, and higher quality!
                </p>
              </div>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Free Plan */}
          <div 
            className={`border-2 ${isFree ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300'} p-4 rounded-lg transition-all relative`}
          >
            {isFree && (
              <div className="absolute -top-3 -right-3 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                CURRENT
              </div>
            )}
            <h3 className="text-xl font-semibold text-gray-700">{CONFIG.tiers.free.name}</h3>
            <div className="text-2xl font-bold my-2">Free</div>
            <ul className="text-gray-600 text-sm mt-2">
              <li className="flex items-center mb-1">
                <svg className="w-4 h-4 mr-1 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {CONFIG.tiers.free.maxUploads} video
              </li>
              <li className="flex items-center mb-1">
                <svg className="w-4 h-4 mr-1 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {CONFIG.tiers.free.maxRecordTime} seconds recording time
              </li>
              <li className="flex items-center">
                <svg className="w-4 h-4 mr-1 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {CONFIG.tiers.free.quality} quality
              </li>
            </ul>
            <div className="mt-4 text-center text-sm text-gray-500">
              Perfect for trying out ReelIntro
            </div>
          </div>
          
          {/* 6-Month Plan */}
          <div 
            onClick={() => selectPlan('sixMonth')}
            className="border-2 border-blue-200 hover:border-blue-500 p-4 rounded-lg cursor-pointer transition-all bg-blue-50"
          >
            <h3 className="text-xl font-semibold text-blue-700">{CONFIG.tiers.sixMonth.name}</h3>
            <div className="text-2xl font-bold my-2">${CONFIG.tiers.sixMonth.displayPrice}</div>
            <ul className="text-gray-600 text-sm mt-2">
              <li className="flex items-center mb-1">
                <svg className="w-4 h-4 mr-1 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-bold">{CONFIG.tiers.sixMonth.maxUploads} videos</span> 
                <span className="ml-1 text-blue-600">(+{CONFIG.tiers.sixMonth.maxUploads - CONFIG.tiers.free.maxUploads})</span>
              </li>
              <li className="flex items-center mb-1">
                <svg className="w-4 h-4 mr-1 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-bold">{CONFIG.tiers.sixMonth.maxRecordTime} seconds</span>
                <span className="ml-1 text-blue-600">(+{CONFIG.tiers.sixMonth.maxRecordTime - CONFIG.tiers.free.maxRecordTime}s)</span>
              </li>
              <li className="flex items-center">
                <svg className="w-4 h-4 mr-1 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {CONFIG.tiers.sixMonth.quality} quality
              </li>
            </ul>
            <button 
              className="mt-4 w-full py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
              onClick={() => selectPlan('sixMonth')}
            >
              Choose 6-Month Plan
            </button>
          </div>
          
          {/* Lifetime Plan */}
          <div 
            onClick={() => selectPlan('lifetime')}
            className="border-2 border-green-200 hover:border-green-500 p-4 rounded-lg cursor-pointer transition-all bg-green-50 relative"
          >
            <div className="absolute -top-3 -right-3 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              BEST VALUE
            </div>
            <h3 className="text-xl font-semibold text-green-700">{CONFIG.tiers.lifetime.name}</h3>
            <div className="text-2xl font-bold my-2">${CONFIG.tiers.lifetime.displayPrice}</div>
            <ul className="text-gray-600 text-sm mt-2">
              <li className="flex items-center mb-1">
                <svg className="w-4 h-4 mr-1 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-bold">{CONFIG.tiers.lifetime.maxUploads} videos</span>
                <span className="ml-1 text-green-600">(+{CONFIG.tiers.lifetime.maxUploads - CONFIG.tiers.free.maxUploads})</span>
              </li>
              <li className="flex items-center mb-1">
                <svg className="w-4 h-4 mr-1 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-bold">{CONFIG.tiers.lifetime.maxRecordTime} seconds</span>
                <span className="ml-1 text-green-600">(+{CONFIG.tiers.lifetime.maxRecordTime - CONFIG.tiers.free.maxRecordTime}s)</span>
              </li>
              <li className="flex items-center">
                <svg className="w-4 h-4 mr-1 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-bold">{CONFIG.tiers.lifetime.quality} quality</span>
                <span className="ml-1 text-green-600">(Upgrade!)</span>
              </li>
            </ul>
            <button 
              className="mt-4 w-full py-2 bg-green-600 text-white rounded hover:bg-green-700 transition"
              onClick={() => selectPlan('lifetime')}
            >
              Choose Lifetime Plan
            </button>
          </div>
        </div>
        
        <div className="flex justify-end">
          <button 
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default PlanSelectionModal;